import { useState, useEffect } from "react";
import {
  Plus,
  Warehouse as WarehouseIcon,
  MapPin,
  Trash2,
  Package,
} from "lucide-react";
import { warehouseApi, productApi } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [warehousesRes, productsRes] = await Promise.all([
        warehouseApi.getAll(),
        productApi.getAll(),
      ]);
      setWarehouses(warehousesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Failed to load warehouses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await warehouseApi.create(formData);
      toast.success("Warehouse created");
      setDialogOpen(false);
      setFormData({ name: "", location: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create warehouse");
    }
  };

  const handleDelete = async (id) => {
    const productsInWarehouse = products.filter((p) => p.warehouse_id === id);
    if (productsInWarehouse.length > 0) {
      toast.error("Cannot delete warehouse with products. Move or delete products first.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this warehouse?")) return;
    try {
      await warehouseApi.delete(id);
      toast.success("Warehouse deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete warehouse");
    }
  };

  const getWarehouseStats = (warehouseId) => {
    const warehouseProducts = products.filter((p) => p.warehouse_id === warehouseId);
    const totalItems = warehouseProducts.reduce((sum, p) => sum + p.quantity, 0);
    const totalValue = warehouseProducts.reduce((sum, p) => sum + p.quantity * p.unit_price, 0);
    const lowStock = warehouseProducts.filter((p) => p.quantity <= p.min_stock).length;
    return { productCount: warehouseProducts.length, totalItems, totalValue, lowStock };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="warehouses-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Warehouses</h1>
          <p className="text-slate-400">Manage storage locations</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition glow-blue"
          data-testid="add-warehouse-btn"
        >
          <Plus className="w-4 h-4" />
          Add Warehouse
        </Button>
      </div>

      {warehouses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {warehouses.map((warehouse) => {
            const stats = getWarehouseStats(warehouse.id);
            return (
              <div
                key={warehouse.id}
                className="bg-[#151923] border border-slate-800 rounded-sm p-6 card-hover group"
                data-testid={`warehouse-card-${warehouse.name}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-amber-500/10 rounded-sm">
                    <WarehouseIcon className="w-6 h-6 text-amber-400" />
                  </div>
                  <button
                    onClick={() => handleDelete(warehouse.id)}
                    className="p-2 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-warehouse-${warehouse.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <h3 className="text-lg font-semibold text-white mb-1">{warehouse.name}</h3>
                {warehouse.location && (
                  <p className="text-sm text-slate-400 flex items-center gap-1 mb-4">
                    <MapPin className="w-3 h-3" />
                    {warehouse.location}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-800">
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Products</p>
                    <p className="text-lg font-mono text-white">{stats.productCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Total Items</p>
                    <p className="text-lg font-mono text-white">{stats.totalItems.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Total Value</p>
                    <p className="text-lg font-mono text-emerald-400">${stats.totalValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-1">Low Stock</p>
                    <p className={`text-lg font-mono ${stats.lowStock > 0 ? "text-red-400" : "text-slate-400"}`}>
                      {stats.lowStock}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-[#151923] border border-slate-800 rounded-sm">
          <div className="empty-state">
            <WarehouseIcon className="empty-state-icon" />
            <p className="text-slate-400 mb-4">No warehouses yet</p>
            <p className="text-sm text-slate-500 mb-4">
              Create a warehouse to start organizing your inventory
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-500"
            >
              Add Your First Warehouse
            </Button>
          </div>
        </div>
      )}

      {/* Add Warehouse Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Warehouse</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Warehouse Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="e.g., Main Warehouse"
                required
                data-testid="warehouse-name-input"
              />
            </div>
            <div>
              <label className="form-label">Location (Optional)</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="e.g., 123 Storage St, City"
                data-testid="warehouse-location-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="warehouse-submit-btn"
              >
                Create Warehouse
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
